import StickyTableResponsive from '../../common/StickyTableResponsive';
import SortableTh from '../../common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import {
  AdminActionButton,
  AdminDeactivateModal,
  AdminDeleteModal,
  AdminPeriodFields,
  AdminValidityActions,
  AdminValidityBadge,
  defaultAdminPeriod,
} from './KkpAdminShared';
import AdminBulkPriceAdjustPanel from '../../admin/AdminBulkPriceAdjustPanel';
import AdminFormLabel from '../../admin/AdminFormLabel';
import SearchableSelect from '../../SearchableSelect';
import KkpPlanningMagInlineCell from '../KkpPlanningMagInlineCell';
import { formatEurCell } from '../../../utils/adminBulkPriceAdjust';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';
import {
  buildProduitRefLabelMap,
  buildProduitRefOptions,
} from '../../../utils/adminProduitRefOptions';
import clientAnnuaireService from '../../../services/clientAnnuaireService';
import kkpService from '../../../services/kkpService';
import pdcService from '../../../services/pdcService';
import { notify } from '../../common/AppToast';
import { AppButton, AppInput, AppModal, AppSpinner } from '../../ui';

const PACK_ASSO_CATEGORIE = 'pack_asso';

const PACK_SORT = {
  libelle: { type: 'string', getValue: (row) => row.libelle },
  valeur: { type: 'number', getValue: (row) => row.valeur_numerique ?? 0 },
  ordre: { type: 'number', getValue: (row) => row.ordre ?? 0 },
  validite: { type: 'string', getValue: (row) => row.validity_label },
};

const emptyPackForm = () => ({
  id: '',
  libelle: '',
  valeur_numerique: '',
  product_ref: '',
  ordre: 0,
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const formatPackPrice = (tarif) => {
  const raw = tarif.effective_price ?? tarif.valeur_numerique;
  if (raw === null || raw === undefined || raw === '') return '—';
  const amount = Number(raw);
  if (!Number.isFinite(amount)) return '—';
  return (
    <>
      {formatEurCell(raw)}
      {tarif.price_source === 'dolibarr_mirror' && (
        <span className="d-block text-xs text-secondary-light">miroir</span>
      )}
    </>
  );
};

const KkpAdminPackAssoSection = () => {
  const [moisOptions, setMoisOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [packs, setPacks] = useState([]);
  const [catalogueProduits, setCatalogueProduits] = useState([]);
  const [pricePushEnabled, setPricePushEnabled] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [form, setForm] = useState(emptyPackForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePeriod, setDeactivatePeriod] = useState(defaultAdminPeriod);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(packs, PACK_SORT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tarifsPayload, catalogue, planning] = await Promise.all([
        pdcService.listAdminTarifs(),
        clientAnnuaireService.listProduits(),
        kkpService.listPlanningAdmin(),
      ]);
      setPacks((tarifsPayload.tarifs || []).filter((item) => item.categorie === PACK_ASSO_CATEGORIE));
      setPricePushEnabled(Boolean(tarifsPayload.price_push_enabled));
      setCatalogueProduits(catalogue);
      setMoisOptions(planning.mois ?? []);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const produitRefOptions = useMemo(
    () => buildProduitRefOptions(
      catalogueProduits,
      [form.product_ref, ...packs.map((item) => item.product_ref)],
    ),
    [catalogueProduits, packs, form.product_ref],
  );
  const produitRefLabelByValue = useMemo(
    () => buildProduitRefLabelMap(produitRefOptions),
    [produitRefOptions],
  );

  const bulkItems = useMemo(
    () => packs
      .filter((item) => {
        const price = item.effective_price ?? item.valeur_numerique;
        return item.validity_status === 'active' && price != null && Number(price) > 0;
      })
      .map((item) => ({
        id: String(item.id),
        groupLabel: item.libelle,
        groupSublabel: item.product_ref || PACK_ASSO_CATEGORIE,
        value: Number(item.effective_price ?? item.valeur_numerique),
        unit: '€',
      })),
    [packs],
  );

  const clearInlineEdit = () => {
    setEditingCell(null);
    setEditDraft(null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyPackForm());
  };

  const openCreateModal = () => {
    clearInlineEdit();
    setForm(emptyPackForm());
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    clearInlineEdit();
    setForm({
      id: item.id,
      libelle: item.libelle ?? '',
      valeur_numerique: item.effective_price ?? item.valeur_numerique ?? '',
      product_ref: item.product_ref ?? '',
      ordre: item.ordre ?? 0,
      valid_from_year: item.valid_from_year ?? new Date().getFullYear(),
      valid_from_month: item.valid_from_month ?? 1,
    });
    setModalOpen(true);
  };

  const submitForm = async () => {
    setSaving(true);
    try {
      await pdcService.upsertAdminTarif({
        id: form.id || undefined,
        categorie: PACK_ASSO_CATEGORIE,
        libelle: form.libelle,
        valeur_numerique: form.valeur_numerique !== '' ? Number(form.valeur_numerique) : null,
        product_ref: form.product_ref || null,
        ordre: Number(form.ordre) || 0,
        valid_from_year: form.valid_from_year,
        valid_from_month: form.valid_from_month,
      });
      notify.success(form.id ? 'Pack enregistré' : 'Pack ajouté');
      closeModal();
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getInlineDraft = (row, field) => {
    if (field === 'libelle') return row.libelle ?? '';
    if (field === 'valeur_numerique') return row.effective_price ?? row.valeur_numerique ?? '';
    if (field === 'product_ref') return String(row.product_ref ?? '').toUpperCase();
    if (field === 'ordre') return row.ordre ?? 0;
    return '';
  };

  const normalizeDraft = (field, draft) => {
    if (field === 'valeur_numerique') {
      if (draft === '' || draft === null || draft === undefined) return null;
      return Number(draft);
    }
    if (field === 'ordre') return Number(draft) || 0;
    if (field === 'product_ref') {
      const ref = typeof draft === 'string' ? draft.trim() : String(draft ?? '').trim();
      return ref !== '' ? ref.toUpperCase() : null;
    }
    return typeof draft === 'string' ? draft.trim() : draft;
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const row = packs.find((item) => String(item.id) === String(editingCell.venteId));
    if (!row) {
      clearInlineEdit();
      return;
    }
    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    const normalizedDraft = normalizeDraft(editingCell.field, draft);
    const normalizedOriginal = normalizeDraft(editingCell.field, getInlineDraft(row, editingCell.field));
    if (String(normalizedDraft ?? '') === String(normalizedOriginal ?? '')) {
      clearInlineEdit();
      return;
    }
    if (editingCell.field === 'libelle' && !String(normalizedDraft ?? '').trim()) {
      notify.error('Le libellé ne peut pas être vide.');
      return;
    }

    setInlineSaving(true);
    try {
      await pdcService.upsertAdminTarif({
        id: row.id,
        categorie: PACK_ASSO_CATEGORIE,
        libelle: editingCell.field === 'libelle' ? normalizedDraft : row.libelle,
        valeur_numerique: editingCell.field === 'valeur_numerique' ? normalizedDraft : row.valeur_numerique,
        product_ref: editingCell.field === 'product_ref' ? normalizedDraft : (row.product_ref ?? null),
        ordre: editingCell.field === 'ordre' ? normalizedDraft : (row.ordre ?? 0),
        valid_from_year: row.valid_from_year,
        valid_from_month: row.valid_from_month,
      });
      setPacks((current) => current.map((item) => {
        if (String(item.id) !== String(row.id)) return item;
        const next = { ...item, [editingCell.field]: normalizedDraft };
        if (editingCell.field === 'valeur_numerique') next.effective_price = normalizedDraft;
        return next;
      }));
      clearInlineEdit();
      notify.success('Modification enregistrée');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const startInlineEdit = async (venteId, field) => {
    if (!venteId || !field) {
      clearInlineEdit();
      return;
    }
    if (editingCell && (editingCell.venteId !== venteId || editingCell.field !== field)) {
      await saveInlineEdit();
    }
    const row = packs.find((item) => String(item.id) === String(venteId));
    if (!row) return;
    setEditingCell({ venteId, field, entity: 'tarif' });
    setEditDraft(getInlineDraft(row, field));
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await pdcService.deactivateAdminTarif(deactivateTarget.id, deactivatePeriod);
      setDeactivateTarget(null);
      notify.success('Pack désactivé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await pdcService.deleteAdminTarif(deleteTarget.id);
      setDeleteTarget(null);
      notify.success('Pack supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkApply = async (updates) => {
    const byId = Object.fromEntries(updates.map((update) => [update.id, update.newValue]));
    setSaving(true);
    try {
      await Promise.all(
        packs
          .filter((item) => byId[String(item.id)] != null)
          .map((item) => pdcService.upsertAdminTarif({
            id: item.id,
            categorie: PACK_ASSO_CATEGORIE,
            libelle: item.libelle,
            valeur_numerique: byId[String(item.id)],
            product_ref: item.product_ref ?? null,
            ordre: item.ordre ?? 0,
            valid_from_year: item.valid_from_year,
            valid_from_month: item.valid_from_month,
          })),
      );
      await load();
      notify.success(
        pricePushEnabled
          ? `${updates.length} pack${updates.length > 1 ? 's' : ''} mis à jour (local + Dolibarr)`
          : `${updates.length} pack${updates.length > 1 ? 's' : ''} mis à jour en local`,
      );
    } catch (err) {
      notify.error(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppSpinner />;

  return (
    <>
      <p className="text-secondary-light small mb-3">
        Formules association proposées dans la grille tarifaire. Le PDF indique le montant HT, avec le net TTC en petite ligne.
      </p>
      <div className="card admin-data-card">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>Packs association ({sortedRows.length})</span>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <AppButton
              size="sm"
              variant={bulkOpen ? 'outline-secondary' : 'danger-600'}
              icon={bulkOpen ? 'mdi:chevron-up' : 'mdi:calculator-variant'}
              disabled={saving || inlineSaving || bulkItems.length === 0}
              onClick={() => setBulkOpen((open) => !open)}
            >
              {bulkOpen ? 'Masquer' : 'Augmenter les prix…'}
            </AppButton>
            <AppButton
              size="sm"
              variant="danger-600"
              icon="mdi:plus"
              disabled={saving || inlineSaving}
              onClick={openCreateModal}
            >
              Ajouter
            </AppButton>
          </div>
        </div>
        <AdminBulkPriceAdjustPanel
          hideHeader
          expanded={bulkOpen}
          onExpandedChange={setBulkOpen}
          title="Augmenter les packs association en groupe"
          description="Uniquement les packs actifs."
          items={bulkItems}
          onApply={handleBulkApply}
          disabled={saving}
          dolibarrPushEnabled={pricePushEnabled}
        />
        <StickyTableResponsive>
          <table className="table table-hover table-sm mb-0">
            <thead>
              <tr>
                <SortableTh sortKey="libelle" label="Libellé" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="valeur" label="Tarif HT" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <th>Réf. Dolibarr</th>
                <SortableTh sortKey="ordre" label="Ordre" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="validite" label="Validité" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh label="Actions" className="text-end" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-secondary-light">Aucun pack pour l’instant.</td>
                </tr>
              )}
              {sortedRows.map((item) => (
                <tr key={item.id} className={item.validity_status !== 'active' ? 'opacity-50' : ''}>
                  <KkpPlanningMagInlineCell
                    venteId={item.id}
                    field="libelle"
                    type="text"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    {item.libelle}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={item.id}
                    field="valeur_numerique"
                    type="number"
                    inputStep="any"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-end"
                  >
                    {formatPackPrice(item)}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={item.id}
                    field="product_ref"
                    type="searchable-select"
                    options={produitRefOptions}
                    allowEmpty
                    emptyLabel="— Aucune —"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-sm"
                  >
                    {produitRefLabelByValue.get(String(item.product_ref || '').toUpperCase()) || item.product_ref || '—'}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={item.id}
                    field="ordre"
                    type="number"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    {item.ordre}
                  </KkpPlanningMagInlineCell>
                  <td>
                    <AdminValidityBadge status={item.validity_status} />
                    <div className="text-xs text-secondary-light mt-1">{item.validity_label}</div>
                  </td>
                  <td className="text-end">
                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                      <AdminActionButton
                        title="Modifier via formulaire"
                        icon="mdi:pencil"
                        className="btn-outline-secondary"
                        disabled={saving || inlineSaving}
                        onClick={() => openEditModal(item)}
                      />
                      <AdminValidityActions
                        item={item}
                        saving={saving || inlineSaving}
                        onDeactivate={(target) => {
                          setDeactivatePeriod(defaultAdminPeriod());
                          setDeactivateTarget({ id: target.id, label: target.libelle });
                        }}
                        onReactivate={(id) => {
                          setSaving(true);
                          pdcService.reactivateAdminTarif(id)
                            .then(() => {
                              notify.success('Pack réactivé');
                              return load();
                            })
                            .catch((err) => notify.error(err.message))
                            .finally(() => setSaving(false));
                        }}
                        onDelete={(target) => setDeleteTarget({ ...target, label: target.libelle })}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyTableResponsive>
      </div>

      <AppModal
        show={modalOpen}
        title={form.id ? 'Modifier un pack association' : 'Ajouter un pack association'}
        onClose={closeModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (event) => {
            event.preventDefault();
            submitForm();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant={form.id ? 'success' : 'danger-600'} loading={saving}>
              {form.id ? 'Enregistrer' : 'Ajouter'}
            </AppButton>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-8">
            <AdminFormLabel help={ADMIN_FIELD_HELP.tarifLibelle.help} helpExample="Pack print (date agenda mag + set)" required>Libellé</AdminFormLabel>
            <AppInput required value={form.libelle} onChange={(event) => setForm((current) => ({ ...current, libelle: event.target.value }))} />
          </div>
          <div className="col-md-4">
            <AdminFormLabel help="Montant HT en euros. Le PDF ajoute le net TTC en petite ligne.">Tarif HT (€)</AdminFormLabel>
            <AppInput type="number" step="any" value={form.valeur_numerique} onChange={(event) => setForm((current) => ({ ...current, valeur_numerique: event.target.value }))} />
          </div>
          <div className="col-md-8">
            <AdminFormLabel help={ADMIN_FIELD_HELP.productRef?.help} helpExample={ADMIN_FIELD_HELP.productRef?.helpExample}>
              Réf. Dolibarr
            </AdminFormLabel>
            <SearchableSelect
              value={form.product_ref}
              onChange={(value) => setForm((current) => ({ ...current, product_ref: value }))}
              options={produitRefOptions}
              allowEmpty
              emptyOptionLabel="— Aucune —"
              placeholder="Produit en vente…"
              emptyLabel="Aucun produit catalogue"
              disabled={saving}
            />
          </div>
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.tarifOrdre.help} helpExample={ADMIN_FIELD_HELP.tarifOrdre.helpExample}>Ordre</AdminFormLabel>
            <AppInput type="number" value={form.ordre} onChange={(event) => setForm((current) => ({ ...current, ordre: event.target.value }))} />
          </div>
          <AdminPeriodFields
            year={form.valid_from_year}
            month={form.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(year) => setForm((current) => ({ ...current, valid_from_year: year }))}
            onMonthChange={(month) => setForm((current) => ({ ...current, valid_from_month: month }))}
          />
        </div>
      </AppModal>

      <AdminDeactivateModal
        target={deactivateTarget}
        period={deactivatePeriod}
        moisOptions={moisOptions}
        saving={saving}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        onPeriodChange={setDeactivatePeriod}
      />
      <AdminDeleteModal
        target={deleteTarget}
        contextKey="tarif"
        saving={saving}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default KkpAdminPackAssoSection;
